/**
 * Previous / Next between outreach threads inside `OutreachThreadModal`, so an owner answering several
 * outreaches in a row never has to close the dialog to reach the next one.
 *
 * The order is the OPENER's: the row list the dialog was opened from hands over its threads in on-screen
 * order (`outreachSiblings`), and the dialog steps through that snapshot — it never refetches to compute a
 * neighbour. A dialog opened from a place with one thread, or no list, shows no stepper. On Home the list
 * mixes RECEIVED threads (`OutreachThreadModal`) and SENT ones (`OutreachCardModal`): each sibling knows
 * which dialog it opens, and both dialogs carry the same stepper.
 *
 * Import-free on purpose (orion-web CLAUDE.md "Tests (jest)").
 */

/**
 * The threads a row list can step through, in its on-screen order: `threadOf` picks a row's thread (or
 * nothing, for a row that does not open this dialog); rows without an id and repeated ids are dropped, the
 * first occurrence winning.
 */
export function outreachSiblings<R, T extends { id?: string }>(rows: readonly R[], threadOf: (row: R) => T | null | undefined): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    const thread = threadOf(row);
    const id = thread?.id;
    if (!thread || !id || seen.has(id)) continue;
    seen.add(id);
    out.push(thread);
  }
  return out;
}

export interface StepperPosition<T> {
  /** 1-based, for "{index} of {total}". */
  index: number;
  total: number;
  /** Null at the first thread — the Previous button is disabled, nothing wraps. */
  previous: T | null;
  /** Null at the last thread. */
  next: T | null;
}

/**
 * Where `currentId` sits in `siblings`, or null when there is no stepper: fewer than two threads, or the
 * current thread is not in the list.
 */
export function threadStepperPosition<T extends { id?: string }>(
  siblings: readonly T[] | null | undefined,
  currentId: string,
): StepperPosition<T> | null {
  if (!siblings || siblings.length < 2 || !currentId) return null;
  const at = siblings.findIndex((s) => s.id === currentId);
  if (at < 0) return null;
  return {
    index: at + 1,
    total: siblings.length,
    previous: at > 0 ? siblings[at - 1] : null,
    next: at < siblings.length - 1 ? siblings[at + 1] : null,
  };
}

export type StepDirection = 'previous' | 'next';

/** The parts of a keydown and its target the key rule reads (a DOM-free shape, for the tests). */
export interface StepKey {
  key: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  defaultPrevented?: boolean;
  isComposing?: boolean;
}
export interface StepKeyTarget {
  tagName?: string;
  isContentEditable?: boolean;
  /** An <input>'s `type`. */
  type?: string;
}

/** <input> types that take no typed text, so the arrows still step while one has focus. */
const NON_TEXT_INPUTS = new Set(['button', 'checkbox', 'radio', 'submit', 'reset', 'image', 'file', 'color', 'range']);

/** Whether focus is in something the arrow keys edit — a text field, a select, a contenteditable. */
export function isTextEntryTarget(target: StepKeyTarget | null | undefined): boolean {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = (target.tagName ?? '').toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUTS.has((target.type ?? 'text').toLowerCase());
  return false;
}

/**
 * ← steps to Previous, → to Next — mirrored in an RTL document (→ Previous, ← Next), the way the chevrons
 * flip (`dir-flip`) and both phones mirror. Only a bare arrow (no modifier, not mid-IME, not already
 * handled), and never while focus is in a text field, where the arrows move the caret.
 */
export function stepKeyDirection(event: StepKey, target: StepKeyTarget | null | undefined, rtl = false): StepDirection | null {
  if (event.defaultPrevented || event.isComposing) return null;
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return null;
  if (isTextEntryTarget(target)) return null;
  if (event.key === 'ArrowLeft') return rtl ? 'next' : 'previous';
  if (event.key === 'ArrowRight') return rtl ? 'previous' : 'next';
  return null;
}
